/**
 * Loading page + menu (spec §5). MenuFlow is the pure state machine; MenuScreen
 * is the DOM. Both screens are drawn in the broadcast register (open-L
 * brackets) over the blueprint paper grid; the loading graphic is a plotter-pen
 * drawing of the disc outline (08 motif), 2D only.
 */
export type LoadStep = 'SOUND BANK' | 'ARENA BAKE' | 'LINK'
export const LOAD_STEPS: readonly LoadStep[] = ['SOUND BANK', 'ARENA BAKE', 'LINK']

/** Spec §5: `?enter=1` (bots, e2e, perf) and the machine entry points (spectate link, team camera, glyph sheet, fidelity fixture) skip loading + menu. */
export type BootMode = 'menu' | 'skip'
export function bootMode(params: URLSearchParams): BootMode {
  return params.get('enter') === '1' || params.has('spectate') || params.has('team') || params.get('glyphs') === '1' || params.has('fixture') ? 'skip' : 'menu'
}

/** Menu live line from `/api/rooms` (RoomInfo[]): humans in every room, and how many match rooms exist. */
export function liveLine(rooms: readonly { kind: string; players: number }[]): string {
  const world = rooms.reduce((a, r) => a + r.players, 0)
  const live = rooms.filter((r) => r.kind === 'match').length
  return `${world} in the world · ${live} ${live === 1 ? 'match' : 'matches'} live`
}

/** Broadcast register (09): open-L brackets, condensed caps, neutral ink on the blueprint paper grid. */
export const MENU_CSS = `
#loading,#menu{position:fixed;inset:0;display:grid;place-items:center;color:#E8ECEF;font-family:"Barlow Condensed","Oswald","Arial Narrow",sans-serif;z-index:30;background:#0f1418;background-image:linear-gradient(rgba(232,236,239,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(232,236,239,.05) 1px,transparent 1px);background-size:32px 32px}
#loading .bk,#menu .bk{position:relative;padding:36px 48px;min-width:360px;text-align:center}
#loading .bk::before,#menu .bk::before,#loading .bk::after,#menu .bk::after{content:"";position:absolute;width:22px;height:22px;border:2px solid #E8ECEF;opacity:.85}
#loading .bk::before,#menu .bk::before{left:0;top:0;border-right:0;border-bottom:0}
#loading .bk::after,#menu .bk::after{right:0;bottom:0;border-left:0;border-top:0}
#menu h1{font-size:44px;letter-spacing:6px;margin:0 0 22px;font-weight:700}
#menu label{display:block;font-size:15px;letter-spacing:2px;margin-bottom:18px;text-transform:uppercase}
#menu input{display:block;margin:8px auto 0;width:240px;background:rgba(232,236,239,.06);border:0;border-bottom:1px solid #E8ECEF;color:#E8ECEF;font:inherit;font-size:20px;letter-spacing:1px;padding:6px 8px;text-align:center}
#menu button{display:block;margin:10px auto 0;background:none;border:0;border-left:2px solid #E8ECEF;border-bottom:2px solid #E8ECEF;color:#E8ECEF;font:inherit;font-size:18px;letter-spacing:2px;padding:8px 22px;cursor:pointer;text-transform:uppercase}
#menu button.dim{opacity:.6;border-color:rgba(232,236,239,.5)}
#menu button:focus{outline:0;color:#E0A33B;border-color:#E0A33B}
#menu .live{margin-top:22px;font-size:13px;letter-spacing:1.5px;opacity:.65;min-height:1.2em}
#loading .bar{width:240px;height:2px;background:rgba(232,236,239,.15);margin:18px auto 10px}
#loading .bar i{display:block;height:100%;width:0;background:#E8ECEF;transition:width .12s linear}
#loading .steps{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12px;letter-spacing:1px;opacity:.7;white-space:pre}
`

export class MenuFlow {
  state: 'loading' | 'menu' | 'entering' | 'in' = 'loading'
  readonly steps: Record<LoadStep, number> = { 'SOUND BANK': 0, 'ARENA BAKE': 0, LINK: 0 }
  constructor(readonly skip: boolean) {}
  progress(step: LoadStep, done01: number): void {
    this.steps[step] = Math.max(0, Math.min(1, done01))
    if (this.state === 'loading' && this.ready) this.state = this.skip ? 'entering' : 'menu'
  }
  get ready(): boolean { return LOAD_STEPS.every((s) => this.steps[s] >= 1) }
  get overall(): number { return LOAD_STEPS.reduce((a, s) => a + this.steps[s], 0) / LOAD_STEPS.length }
  enter(): boolean { if (this.state !== 'menu') return false; this.state = 'entering'; return true }
  entered(): void { this.state = 'in' }
}

export class MenuScreen {
  private readonly nameInput: HTMLInputElement
  private readonly enterBtn: HTMLButtonElement
  private readonly liveEl: HTMLElement
  private readonly barEl: HTMLElement
  private readonly stepsEl: HTMLElement
  private readonly pen: HTMLCanvasElement
  constructor(private readonly loadingEl: HTMLElement, private readonly menuEl: HTMLElement, cb: { onEnter(name: string): void; onOptions(): void }, initialName: string) {
    const doc = loadingEl.ownerDocument
    const style = doc.createElement('style'); style.textContent = MENU_CSS; doc.head.appendChild(style)
    loadingEl.innerHTML = `<div class="bk"><canvas id="pen" width="240" height="240"></canvas><div class="bar"><i></i></div><div class="steps"></div></div>`
    menuEl.innerHTML = `<div class="bk"><h1>DISCLIKE</h1><label>NAME <input id="mname" maxlength="16" autocomplete="off" /></label><button id="menter">ENTER DISCWORLD</button><button id="moptions" class="dim">OPTIONS</button><div class="live"></div></div>`
    this.nameInput = menuEl.querySelector('#mname')!; this.nameInput.value = initialName
    this.enterBtn = menuEl.querySelector('#menter')!
    this.liveEl = menuEl.querySelector('.live')!
    this.barEl = loadingEl.querySelector('.bar > i')!
    this.stepsEl = loadingEl.querySelector('.steps')!
    this.pen = loadingEl.querySelector('#pen')!
    this.enterBtn.onclick = () => cb.onEnter(this.name)
    ;(menuEl.querySelector('#moptions') as HTMLButtonElement).onclick = cb.onOptions
    this.nameInput.onkeydown = (e) => { if (e.key === 'Enter') cb.onEnter(this.name) }
  }
  get name(): string { return this.nameInput.value.trim() }
  render(flow: MenuFlow, live: string | null, now: number): void {
    this.loadingEl.style.display = flow.state === 'loading' ? '' : 'none'
    this.menuEl.style.display = flow.state === 'menu' ? '' : 'none'
    if (flow.state === 'loading') {
      this.barEl.style.width = `${Math.round(flow.overall * 100)}%`
      this.stepsEl.textContent = LOAD_STEPS.map((s) => `${s} ${flow.steps[s] >= 1 ? '■' : flow.steps[s] > 0 ? '▪' : '□'}`).join('   ')
      drawPen(this.pen.getContext('2d')!, flow.overall, now)
    }
    if (flow.state === 'menu') this.liveEl.textContent = live ?? ''
    if (flow.state === 'menu' && document.activeElement !== this.nameInput) this.enterBtn.focus()
  }
}

/** Plotter-pen disc: the outline is drawn on in proportion to progress; the pen dot rides the tip. */
function drawPen(ctx: CanvasRenderingContext2D, t01: number, now: number): void {
  const W = ctx.canvas.width, H = ctx.canvas.height, cx = W / 2, cy = H / 2, R = 88
  ctx.clearRect(0, 0, W, H)
  ctx.strokeStyle = 'rgba(232,236,239,0.85)'; ctx.lineWidth = 2; ctx.lineCap = 'round'
  const end = -Math.PI / 2 + t01 * Math.PI * 2
  ctx.beginPath(); ctx.arc(cx, cy, R, -Math.PI / 2, end); ctx.stroke()
  ctx.strokeStyle = 'rgba(232,236,239,0.35)'; ctx.lineWidth = 1
  ctx.beginPath(); ctx.arc(cx, cy, R * 0.55, -Math.PI / 2, -Math.PI / 2 + Math.max(0, t01 - 0.5) * 2 * Math.PI * 2); ctx.stroke()
  const px = cx + Math.cos(end) * R, py = cy + Math.sin(end) * R
  ctx.fillStyle = '#e0a33b'; ctx.beginPath(); ctx.arc(px, py, 3 + Math.sin(now / 90) * 0.5, 0, Math.PI * 2); ctx.fill()
}
